import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { RevealLines } from "@/components/motion/Reveal";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const opacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  // Manual split for artistic layout
  const lines = ["Consultoría en sistemas", "de gestión ISO", "en Costa Rica."];

  return (
    <section id="inicio" ref={ref} className="relative min-h-[100svh] pt-[72px] overflow-clip" data-testid="hero-section">
      <div className="relative mx-auto max-w-[1440px] px-5 md:px-10 pt-14 md:pt-24 pb-16 md:pb-20">
        {/* Top row: eyebrow + year */}
        <motion.div
          initial={false}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="flex items-center justify-between text-[11px] tracking-[0.22em] uppercase text-[#4A4A4A]"
        >
          <span>KB Consultoría · Est. Costa Rica</span>
          <span className="hidden sm:inline">Sistemas de gestión · ISO · Sostenibilidad</span>
        </motion.div>

        {/* Title */}
        <h1 className="mt-10 md:mt-16 font-serif text-[#1A1A1A] text-[clamp(3rem,10.5vw,10.5rem)] leading-[0.9] tracking-[-0.02em]">
          <RevealLines lines={lines} delay={0.35} stagger={0.15} />
          <span className="inline-block ml-3 text-[#1B3B2B] italic font-light" aria-hidden="true">
            <motion.span initial={false} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.15, duration: 0.6 }}>—</motion.span>
          </span>
        </h1>

        {/* Sub row: image + copy */}
        <div className="mt-12 md:mt-16 grid grid-cols-12 gap-6 md:gap-10 items-end">
          {/* Copy + CTAs */}
          <motion.div
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.7 }}
            className="col-span-12 md:col-span-6 lg:col-span-5"
          >
            <p className="text-[#1A1A1A]/85 text-base md:text-lg leading-relaxed max-w-md">
              Acompañamos a organizaciones de Costa Rica y Centroamérica en la implementación,
              auditoría y mejora de sus <em className="font-serif italic text-[#1B3B2B]"> sistemas de gestión</em>,
              mediante soluciones personalizadas, prácticas y orientadas a resultados.
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <a href="#contacto" data-testid="hero-cta-primary" className="kb-btn">
                Solicitar una asesoría <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
              <a href="#servicios" data-testid="hero-cta-secondary" className="kb-btn ghost">
                Conocer nuestros servicios
              </a>
            </div>

            <p className="mt-8 text-xs text-[#4A4A4A] max-w-[420px] italic font-serif text-base leading-snug">
              “Soluciones adaptadas a la realidad, necesidades y objetivos de cada organización.”
            </p>
          </motion.div>

          {/* Image spotlight with parallax */}
          <div className="col-span-12 md:col-span-6 lg:col-span-7 relative">
            <motion.div
              initial={false}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.9, duration: 1.1, ease: [0.76, 0, 0.24, 1] }}
              style={{ y, scale, opacity }}
              className="relative"
            >
              <div className="relative aspect-[4/5] md:aspect-[5/6] lg:aspect-[16/12] overflow-hidden spotlight-frame">
                <picture>
                  <source
                    type="image/webp"
                    srcSet="/hero-consultoria-800.webp 800w, /hero-consultoria-1200.webp 1200w, /hero-consultoria-1600.webp 1600w"
                    sizes="(max-width: 767px) 100vw, 58vw"
                  />
                  <img
                    src="/hero-consultoria-1200.webp"
                    alt="Consultores revisando documentos y sistemas de gestión ISO"
                    width="1200"
                    height="800"
                    className="absolute inset-0 w-full h-full object-cover duotone"
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                  />
                </picture>
                {/* Editorial caption overlay */}
                <div className="absolute left-4 bottom-4 text-[10px] tracking-[0.22em] uppercase text-white/85">
                  001 · Consultoría estratégica
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll cue */}
        <motion.div
          initial={false}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 0.6 }}
          className="mt-16 flex items-center gap-3 text-[11px] tracking-[0.22em] uppercase text-[#4A4A4A]"
        >
          <ArrowDown className="w-4 h-4 animate-bounce" />
          Desliza para explorar
        </motion.div>
      </div>
    </section>
  );
}
